import isEqual from 'lodash/isEqual'

import type {
  ZoneBoxTarget,
  ZoneChangeGuard,
  ZoneParams,
  ZoneRectangle,
} from './zoneAnalysisApi'

export const patchZoneById = (
  zones: ZoneParams[],
  zoneId: string,
  updates: Partial<ZoneParams>,
  guard?: ZoneChangeGuard
): ZoneParams[] => {
  const current = zones.find((zone) => zone.id === zoneId)
  if (
    !current ||
    (guard &&
      (current.zone_reference_image_id !== guard.zone_reference_image_id ||
        !isEqual(current.drawing_bounds, guard.drawing_bounds)))
  )
    return zones

  const next = zones.map((zone) =>
    zone.id === zoneId ? { ...zone, ...updates } : zone
  )
  return isEqual(next, zones) ? zones : next
}

export const removeZoneById = (
  zones: ZoneParams[],
  zoneId: string
): ZoneParams[] => {
  const next = zones.filter((zone) => zone.id !== zoneId)
  return next.length === zones.length ? zones : next
}

export const appendZoneBox = (
  zones: ZoneParams[],
  zoneId: string,
  rectangle: ZoneRectangle
): ZoneParams[] => {
  const zone = zones.find((item) => item.id === zoneId)
  if (!zone) return zones
  return zones.map((item) =>
    item.id === zoneId
      ? { ...item, drawing_bounds: [...(item.drawing_bounds ?? []), rectangle] }
      : item
  )
}

export const replaceZoneBox = (
  zones: ZoneParams[],
  target: ZoneBoxTarget,
  rectangle: ZoneRectangle
): ZoneParams[] => {
  const zone = zones.find((item) => item.id === target.zoneId)
  const boxes = zone?.drawing_bounds
  if (!boxes || target.boxIndex < 0 || target.boxIndex >= boxes.length)
    return zones

  return zones.map((item) =>
    item.id === target.zoneId
      ? {
          ...item,
          drawing_bounds: boxes.map((box, index) =>
            index === target.boxIndex ? { ...box, ...rectangle } : box
          ),
        }
      : item
  )
}

export const removeZoneBox = (
  zones: ZoneParams[],
  target: ZoneBoxTarget
): ZoneParams[] => {
  const zone = zones.find((item) => item.id === target.zoneId)
  const boxes = zone?.drawing_bounds
  if (!boxes || target.boxIndex < 0 || target.boxIndex >= boxes.length)
    return zones

  return zones.map((item) =>
    item.id === target.zoneId
      ? {
          ...item,
          drawing_bounds: boxes.filter((_, index) => index !== target.boxIndex),
        }
      : item
  )
}

export const replaceImageZoneProposals = (
  zones: ZoneParams[],
  imageId: string,
  proposals: ZoneParams[]
): ZoneParams[] => {
  const keptZones = zones.filter(
    (zone) => !zone.ai_suggested || zone.zone_reference_image_id !== imageId
  )
  return keptZones.length === zones.length && proposals.length === 0
    ? zones
    : [...keptZones, ...proposals]
}
